
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Device, Message, RangeStatus } from './types';
import { discovery } from './DiscoveryService';
import { webrtc } from './webrtcService';
import { db } from './db';
import QRCode from 'qrcode';
import { Html5Qrcode } from 'html5-qrcode';
import {
  DeviceIcon,
  WifiIcon,
  HistoryIcon,
  PinIcon,
  SendIcon,
  FileIcon,
  LinkIcon,
  CloseIcon,
  UserIcon,
  DhakadLabsLogo,
  QRIcon,
  CameraIcon,
  TrashIcon
} from './components/Icons';

/**
 * Custom High-Fidelity Indian Flag Component
 * Designed to look exactly like the requested high-quality emoji with very rounded corners.
 */
const IndianFlagIcon = ({ size = 24 }: { size?: number }) => (
  <svg
    width={size}
    height={(size * 3) / 4}
    viewBox="0 0 100 75"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="inline-block align-middle rounded-[20%] overflow-hidden shadow-lg border border-white/10"
  >
    <rect width="100" height="75" fill="white" />
    <path d="M0 0H100V25H0V0Z" fill="#FF9933" />
    <path d="M0 50H100V75H0V50Z" fill="#138808" />
    <circle cx="50" cy="37.5" r="9" stroke="#000080" strokeWidth="1.5" />
    <circle cx="50" cy="37.5" r="2" fill="#000080" />
    {[...Array(24)].map((_, i) => (
      <line
        key={i}
        x1="50" y1="37.5"
        x2={50 + 9 * Math.cos((i * 15 * Math.PI) / 180)}
        y2={37.5 + 9 * Math.sin((i * 15 * Math.PI) / 180)}
        stroke="#000080"
        strokeWidth="0.5"
      />
    ))}
  </svg>
);

type ViewState = 'landing' | 'app' | 'privacy' | 'terms' | 'disclaimer' | 'contact' | 'about' | 'copyright';
type AppTab = 'discovery' | 'activity';

const App: React.FC = () => {
  const [view, setView] = useState<ViewState>('landing');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);

  const renderView = () => {
    switch (view) {
      case 'landing':
        return <LandingPage onEnter={() => setView('app')} setView={setView} />;
      case 'app':
        return <MainDashboard onBackToLanding={() => setView('landing')} />;
      case 'privacy':
        return <LegalPage title="Privacy Policy" onBack={() => setView('landing')} content={<PrivacyPolicyContent />} />;
      case 'terms':
        return <LegalPage title="Terms & Conditions" onBack={() => setView('landing')} content={<TermsContent />} />;
      case 'disclaimer':
        return <LegalPage title="Disclaimer" onBack={() => setView('landing')} content={<DisclaimerContent />} />;
      case 'copyright':
        return <LegalPage title="Copyright Policy" onBack={() => setView('landing')} content={<CopyrightPolicyContent />} />;
      case 'contact':
        return <LegalPage title="Contact" onBack={() => setView('landing')} content={<ContactContent />} />;
      case 'about':
        return <AboutPage onBack={() => setView('landing')} />;
      default:
        return <LandingPage onEnter={() => setView('app')} setView={setView} />;
    }
  };

  return renderView();
};

const Header: React.FC<{
  onAction: () => void;
  setView: (v: ViewState) => void;
  actionText?: string;
}> = ({ onAction, setView, actionText = "Enter WebApp" }) => (
  <header className="fixed top-3 sm:top-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-5xl z-50">
    <nav className="h-12 sm:h-14 px-4 sm:px-7 glass-nav rounded-full flex items-center justify-between">
      <div className="flex items-center gap-2.5 cursor-pointer group" onClick={() => setView('landing')}>
        <img src="/Filespread-fevicon.png" alt="FileSpread Logo" className="w-[26px] h-[26px] object-contain transition-transform group-hover:scale-110" />
        <span className="font-display text-sm sm:text-base text-white whitespace-nowrap" style={{ fontWeight: 700, letterSpacing: '-0.01em' }}>FileSpread</span>
      </div>
      <button onClick={onAction} className="px-4 py-1.5 sm:px-5 sm:py-2 bg-orange-gold text-[#0B0E14] text-[10px] sm:text-xs rounded-full transition-all active:scale-95 shadow-orange-glow whitespace-nowrap font-display" style={{ fontWeight: 700, letterSpacing: '0.05em' }}>
        {actionText}
      </button>
    </nav>
  </header>
);

const FooterSection: React.FC = () => (
  <footer className="py-6 text-center shrink-0 w-full mt-auto" style={{ borderTop: '1px solid rgba(255,255,255,0.05)', background: 'rgba(9,11,17,0.5)' }}>
    <p className="font-mono text-[9px] tracking-widest uppercase" style={{ color: '#64748B' }}>Made with ♡ by <span style={{ color: '#FF8A00' }}>Dhakad Labs</span></p>
  </footer>
);

const LegalPage: React.FC<{ title: string; onBack: () => void; content: React.ReactNode }> = ({ title, onBack, content }) => (
  <div className="min-h-screen dark-gradient text-[#E5E7EB] selection:bg-[#FF8A00]/30 pt-24 flex flex-col">
    <Header onAction={onBack} setView={() => onBack()} actionText="Back Home" />
    <main className="max-w-3xl mx-auto px-6 py-12 flex-1 w-full">
      <p className="font-mono text-[10px] tracking-widest uppercase mb-3" style={{ color: '#FF8A00' }}>Legal</p>
      <h1 className="font-display text-3xl sm:text-5xl font-bold text-white mb-6 tracking-tight" style={{ fontWeight: 700 }}>{title}</h1>
      <div className="mb-8" style={{ height: '1px', background: 'linear-gradient(90deg,transparent,rgba(255,138,0,0.25),transparent)' }} />
      <div className="prose prose-invert max-w-none text-slate-400 leading-relaxed">{content}</div>
    </main>
    <FooterSection />
  </div>
);


const LandingPage: React.FC<{ onEnter: () => void; setView: (v: ViewState) => void }> = ({ onEnter, setView }) => {
  return (
    <div className="min-h-screen dark-gradient selection:bg-[#FF8A00]/30 selection:text-white overflow-x-hidden flex flex-col">
      <Header onAction={onEnter} setView={setView} actionText="Open App" />

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-24 sm:pt-36 pb-10 sm:pb-16 w-full text-center">
        <div className="flex justify-center gap-2 mb-6">
          <span className="tag-pill"><span className="w-1.5 h-1.5 rounded-full bg-[#FF8A00] animate-pulse inline-block mr-1" />Made in Bharat <IndianFlagIcon size={14} /></span>
          <span className="tag-pill"><span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block mr-1" />Browser-native · No install</span>
        </div>
        <h1 className="font-display text-[2.4rem] sm:text-7xl md:text-[5.5rem] font-bold text-white tracking-tight leading-[1.05] mb-5" style={{ fontWeight: 700 }}>
          No cloud. No servers. Just peers.<br /><span className="text-orange-gold">Fast, private, local sharing.</span>
        </h1>
        <p className="max-w-lg mx-auto text-slate-400 text-base sm:text-lg leading-relaxed mb-8">
          FileSpread moves files, text and links between your devices over WebRTC — peer to peer, end to end, cloud nowhere in sight.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-14">
          <button onClick={onEnter} className="w-full sm:w-auto px-8 py-3.5 bg-orange-gold text-[#0B0E14] font-display font-bold rounded-xl shadow-orange-glow active:scale-95 transition-all text-sm" style={{ fontWeight: 700 }}>Open FileSpread →</button>
          <a href="https://dhakadlabs.site/#support" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto px-8 py-3.5 glass-card text-white font-display font-bold rounded-xl active:scale-95 transition-all text-sm text-center hover:border-white/20" style={{ fontWeight: 600, border: '1px solid rgba(255,255,255,0.1)' }}>Support Us</a>
        </div>

        {/* ── SIGNATURE: Signal-sweep P2P diagram ─────────────────── */}
        <div className="relative max-w-2xl mx-auto">
          <div className="absolute -inset-8 rounded-3xl blur-[60px] opacity-[0.07]" style={{ background: '#FF8A00' }} />
          <div className="relative glass-card rounded-2xl overflow-hidden p-6 sm:p-8" style={{ border: '1px solid rgba(255,138,0,0.12)' }}>
            <div className="flex items-center justify-between mb-5">
              <span className="font-mono text-[9px] tracking-widest uppercase" style={{ color: '#64748B' }}>webrtc · e2e encrypted</span>
              <span className="tag-pill"><span className="w-1.5 h-1.5 rounded-full bg-[#FF8A00] animate-pulse inline-block mr-1" />Live channel</span>
            </div>
            <div className="relative flex items-center justify-between gap-3">
              {/* Device A */}
              <div className="flex flex-col items-center gap-2 shrink-0">
                <div className="relative">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 glass-inset rounded-xl flex items-center justify-center accent-glow" style={{ border: '1px solid rgba(255,138,0,0.2)' }}>
                    <span style={{ color: '#FF8A00' }}><DeviceIcon type="laptop" className="w-6 h-6 sm:w-8 sm:h-8" /></span>
                  </div>
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-green-400 border-2 border-[#0B0E14]" />
                </div>
                <span className="font-mono text-[8px] tracking-widest uppercase" style={{ color: '#64748B' }}>Your device</span>
              </div>
              {/* Signal line — THE SIGNATURE */}
              <div className="flex-1 relative" style={{ height: '36px' }}>
                <div className="absolute top-1/2 left-0 right-0" style={{ height: '1px', background: 'linear-gradient(90deg,rgba(255,138,0,0.1) 0%,rgba(255,215,0,0.3) 50%,rgba(255,138,0,0.1) 100%)', transform: 'translateY(-50%)' }} />
                <div className="absolute top-0 bottom-0" style={{ width: '60px', background: 'linear-gradient(90deg,transparent,rgba(255,138,0,0.9),rgba(255,215,0,1),rgba(255,138,0,0.9),transparent)', animation: 'signal-sweep 2.2s cubic-bezier(0.4,0,0.6,1) infinite', filter: 'blur(1.5px)', borderRadius: '4px' }} />
                <div className="absolute top-1/2" style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#FFD700', boxShadow: '0 0 8px #FF8A00', transform: 'translateY(-50%)', animation: 'data-packet 2.2s ease-in-out infinite' }} />
                <div className="absolute top-1/2" style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#FF8A00', boxShadow: '0 0 5px #FF8A00', transform: 'translateY(calc(-50% + 5px))', animation: 'data-packet-rev 2.2s ease-in-out infinite 1.1s' }} />
                <div className="absolute top-full mt-1.5 left-1/2 -translate-x-1/2"><span className="font-mono text-[7px] tracking-widest uppercase whitespace-nowrap" style={{ color: 'rgba(255,138,0,0.6)' }}>P2P · no server relay</span></div>
              </div>
              {/* Device B */}
              <div className="flex flex-col items-center gap-2 shrink-0">
                <div className="relative">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 glass-inset rounded-xl flex items-center justify-center accent-glow" style={{ border: '1px solid rgba(255,138,0,0.2)' }}>
                    <span style={{ color: '#FF8A00' }}><DeviceIcon type="mobile" className="w-6 h-6 sm:w-8 sm:h-8" /></span>
                  </div>
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-green-400 border-2 border-[#0B0E14]" style={{ animationDelay: '0.4s' }} />
                </div>
                <span className="font-mono text-[8px] tracking-widest uppercase" style={{ color: '#64748B' }}>Their device</span>
              </div>
            </div>
            <div className="mt-6 pt-5 grid grid-cols-2 sm:grid-cols-4 gap-4" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              {[['Files', 'Direct transfer'], ['Text', 'End-to-end'], ['Links', 'WebRTC only'], ['Data', 'P2P Transfer']].map(([l, s]) => (
                <div key={l}><p className="font-display text-white text-sm font-bold" style={{ fontWeight: 600 }}>{l}</p><p className="font-mono text-[8px] tracking-wider uppercase mt-0.5" style={{ color: '#64748B' }}>{s}</p></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div style={{ height: '1px', background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.06),transparent)', maxWidth: '64rem', margin: '0 auto', width: '100%' }} />

      {/* ── Community free section ───────────────────────────────── */}
      <section className="py-14 sm:py-20 w-full">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-6 mb-8">
            <div><p className="font-mono text-[10px] tracking-widest uppercase mb-2" style={{ color: '#FF8A00' }}>Community</p><h2 className="font-display text-2xl sm:text-4xl font-bold text-white" style={{ fontWeight: 700 }}>Always free.</h2></div>
            <p className="font-body text-slate-400 text-sm max-w-xs mt-1">Every feature, every transfer — no paywall, no account required, no strings.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[['🎁', '100% Free', 'No hidden charges'], ['🚀', 'No Subscription', 'All features unlocked']].map(([e, t, s]) => (
              <div key={t} className="glass-card rounded-2xl p-6 border hover-glow transition-all" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                <div className="text-2xl mb-3">{e}</div>
                <h3 className="font-display font-bold text-white text-base mb-1" style={{ fontWeight: 600 }}>{t}</h3>
                <p className="font-mono text-[8px] tracking-wider uppercase" style={{ color: '#64748B' }}>{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div style={{ height: '1px', background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.06),transparent)', maxWidth: '64rem', margin: '0 auto', width: '100%' }} />

      {/* ── Feature grid ─────────────────────────────────────────── */}
      <section id="features" className="py-14 sm:py-24 w-full scroll-mt-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="mb-10"><p className="font-mono text-[10px] tracking-widest uppercase mb-2" style={{ color: '#FF8A00' }}>Capabilities</p><h2 className="font-display text-2xl sm:text-4xl font-bold text-white" style={{ fontWeight: 700 }}>Built for sharing</h2></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureCard icon={<DeviceIcon type="laptop" />} title="Cross Platform" description="Works on any device with any modern browser - iOS, Android, Windows, Mac, Linux." />
            <FeatureCard icon={<img src="/Filespread-fevicon.png" alt="FileSpread" className="w-12 h-12 object-contain" />} title="No Installation" description="Open the URL and start sharing. No app store, no download, no account." />
            <FeatureCard icon={<HistoryIcon size={24} />} title="P2P Security" description="Data flows directly between devices. WebRTC encrypted. No server sees your files." />
            <FeatureCard icon={<SendIcon />} title="Full Speed" description="Local network speed, no cloud bottleneck. Limited only by your hardware." />
          </div>
        </div>
      </section>

      <div style={{ height: '1px', background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.06),transparent)', maxWidth: '64rem', margin: '0 auto', width: '100%' }} />

      {/* ── About section ────────────────────────────────────────── */}
      <section id="about-us" className="py-14 sm:py-24 w-full">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-14 items-center">
            <div className="order-2 lg:order-1">
              <p className="font-mono text-[10px] tracking-widest uppercase mb-3" style={{ color: '#FF8A00' }}>Our vision</p>
              <h2 className="font-display text-3xl sm:text-5xl font-bold text-white mb-5 tracking-tight leading-tight" style={{ fontWeight: 700 }}>Private by design,<br /><span className="text-orange-gold">local by default.</span></h2>
              <p className="font-body text-slate-400 text-base leading-relaxed mb-4">FileSpread was built to move data between devices without giving it to a third-party server. Your local network is the safest, fastest path.</p>
              <p className="font-body text-slate-500 text-sm leading-relaxed mb-7">No accounts. No tracking. No cloud latency.</p>
              <button onClick={() => setView('about')} className="font-mono text-[10px] tracking-widest uppercase flex items-center gap-2 group transition-all" style={{ color: '#FF8A00' }}>About Dhakad Labs <span className="group-hover:translate-x-1 transition-transform">→</span></button>
            </div>
            <div className="order-1 lg:order-2 glass-card rounded-2xl sm:rounded-3xl p-10 sm:p-14 flex items-center justify-center accent-glow mx-auto w-full max-w-sm lg:max-w-none" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
              <DhakadLabsLogo className="w-full h-auto max-w-[180px] sm:max-w-[240px]" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────── */}
      <footer className="py-12 sm:py-14 mt-auto w-full" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(9,11,17,0.4)' }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <a href="https://buymeacoffee.com/dhakad" target="_blank" rel="noopener noreferrer" className="px-7 py-3 bg-orange-gold text-[#0B0E14] rounded-xl font-display font-bold text-xs tracking-wide shadow-orange-glow transition-all hover:scale-[1.02] text-center" style={{ fontWeight: 700 }}>Support Us</a>
            <a href="https://dhakadlabs.site/#support" target="_blank" rel="noopener noreferrer" className="px-7 py-3 glass-card text-white rounded-xl font-display font-bold text-xs tracking-wide transition-all text-center hover:border-white/20" style={{ fontWeight: 600, border: '1px solid rgba(255,255,255,0.1)' }}>Support Us</a>
          </div>
          <div style={{ height: '1px', background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.06),transparent)', marginBottom: '1.5rem' }} />
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-5">
            <div className="flex items-center gap-2"><img src="/Filespread-fevicon.png" alt="FileSpread" className="w-7 h-7 object-contain" /><span className="font-display font-bold text-white text-sm" style={{ fontWeight: 700 }}>FileSpread</span></div>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {(['privacy', 'terms', 'disclaimer', 'copyright', 'contact', 'about'] as ViewState[]).map(v => (
                <button key={v} onClick={() => setView(v)} className="font-mono text-[9px] tracking-widest uppercase transition-colors hover:text-[#FF8A00]" style={{ color: '#64748B' }}>{v === 'privacy' ? 'Privacy' : v === 'terms' ? 'Terms' : v === 'copyright' ? 'Copyright' : v.charAt(0).toUpperCase() + v.slice(1)}</button>
              ))}
            </div>
          </div>
          <div className="mt-6 flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="font-mono text-[9px] tracking-widest uppercase" style={{ color: '#64748B' }}>Made with ♡ by <span style={{ color: '#FF8A00' }}>Dhakad Labs</span></p>
            <p className="font-mono text-[9px] tracking-widest uppercase" style={{ color: '#334155' }}>© 2026 Dhakad Labs</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const PairingRequestModal: React.FC<{ fromName: string; onAccept: () => void; onDecline: () => void }> = ({ fromName, onAccept, onDecline }) => (
  <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/70 backdrop-blur-xl">
    <div className="glass-card border border-orange-gold/30 w-full max-w-sm rounded-[2.5rem] p-8 shadow-2xl text-center transform scale-100 animate-in fade-in zoom-in duration-300" style={{ background: 'rgba(22,26,34,0.82)', backdropFilter: 'blur(32px) saturate(180%)', WebkitBackdropFilter: 'blur(32px) saturate(180%)' }}>
      <div className="w-20 h-20 bg-orange-gold/10 text-orange-gold rounded-full flex items-center justify-center mx-auto mb-6 accent-glow">
        <WifiIcon size={40} />
      </div>
      <h2 className="text-2xl font-black text-white mb-2">Incoming Pair</h2>
      <p className="text-slate-400 text-sm mb-8 leading-relaxed px-2">
        <span className="text-orange-gold font-black break-words">{fromName}</span> wants to bridge with your device securely.
      </p>
      <div className="grid grid-cols-2 gap-4">
        <button onClick={onDecline} className="py-4 bg-slate-800 text-white font-bold rounded-2xl hover:bg-slate-700 transition-all active:scale-95 text-xs sm:text-sm">Decline</button>
        <button onClick={onAccept} className="py-4 bg-orange-gold text-[#0B0E14] font-black rounded-2xl shadow-orange-glow active:scale-95 text-xs sm:text-sm">Accept</button>
      </div>
    </div>
  </div>
);

const QRScannerModal: React.FC<{ onClose: () => void; onScan: (peerId: string) => void }> = ({ onClose, onScan }) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const containerId = "qr-reader";
  const [error, setError] = useState<string | null>(null);

  const startScanner = async () => {
    setError(null);
    try {
      const html5QrCode = new Html5Qrcode(containerId);
      scannerRef.current = html5QrCode;
      const config = { fps: 10, qrbox: { width: 250, height: 250 } };
      await html5QrCode.start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          try {
            const data = JSON.parse(decodedText);
            if (data.type === 'filespread-connect' && data.id) {
              onScan(data.id);
              stopAndClose();
            }
          } catch (e) { }
        },
        () => { }
      );
    } catch (err: any) {
      setError("Camera access denied or unavailable.");
    }
  };

  useEffect(() => {
    startScanner();
    return () => { stopAndClose(); };
  }, []);

  const stopAndClose = async () => {
    if (scannerRef.current?.isScanning) {
      await scannerRef.current.stop();
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 sm:p-6 bg-black/90 backdrop-blur-xl">
      <div className="glass-card border border-white/5 w-full max-w-md rounded-[2.5rem] p-6 shadow-2xl relative overflow-hidden" style={{ background: 'rgba(18,21,28,0.85)', backdropFilter: 'blur(32px) saturate(200%)', WebkitBackdropFilter: 'blur(32px) saturate(200%)' }}>
        <div className="absolute top-0 left-0 w-full h-1 bg-orange-gold animate-pulse"></div>
        <button onClick={onClose} className="absolute top-4 right-4 z-30 text-slate-500 hover:text-white bg-[#0B0E14]/80 p-2 rounded-full transition-all">
          <CloseIcon size={20} />
        </button>
        <div className="text-center mb-6">
          <CameraIcon size={28} className="mx-auto mb-3 text-orange-gold" />
          <h2 className="text-xl font-extrabold text-white">Scan to Connect</h2>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">Point your camera at a FileSpread QR</p>
        </div>
        {error && <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-bold rounded-xl mb-4 text-center">{error}</div>}
        <div id={containerId} className="w-full aspect-square bg-[#0B0E14] rounded-3xl overflow-hidden border border-slate-800 relative z-10 shadow-inner"></div>
      </div>
    </div>
  );
};

const MyQRModal: React.FC<{ device: Device; networkId: string; onClose: () => void }> = ({ device, networkId, onClose }) => {
  const [qrDataUrl, setQrDataUrl] = useState<string>('');

  useEffect(() => {
    const payload = JSON.stringify({ type: 'filespread-connect', id: device.id, gid: networkId, name: device.name });
    QRCode.toDataURL(payload, {
      width: 512,
      margin: 1,
      color: { dark: '#FF8A00', light: '#0B0E14' }
    }).then(setQrDataUrl);
  }, [device, networkId]);

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 sm:p-8 bg-black/80 backdrop-blur-xl">
      <div className="glass-card border border-white/5 w-full max-w-sm rounded-[2.5rem] sm:rounded-[3rem] p-8 shadow-2xl relative text-center" style={{ background: 'rgba(18,21,28,0.85)', backdropFilter: 'blur(32px) saturate(200%)', WebkitBackdropFilter: 'blur(32px) saturate(200%)' }}>
        <button onClick={onClose} className="absolute top-6 right-6 text-slate-500 hover:text-white p-2">
          <CloseIcon size={24} />
        </button>
        <h2 className="text-2xl font-black text-white mb-2">My Grid ID</h2>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-8">Share this to receive files</p>
        <div className="glass-inset p-4 sm:p-6 rounded-[2rem] sm:rounded-[2.5rem] border border-white/[0.05] mb-8">
          {qrDataUrl && <img src={qrDataUrl} className="w-full rounded-2xl shadow-2xl" alt="Your QR Code" />}
        </div>
        <div className="flex flex-col gap-3">
          <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] truncate px-4">{device.name}</p>
          <button onClick={onClose} className="w-full py-4 bg-orange-gold text-[#0B0E14] font-black rounded-2xl shadow-orange-glow transition-all transform active:scale-95">Done</button>
        </div>
      </div>
    </div>
  );
};

const MainDashboard: React.FC<{ onBackToLanding: () => void }> = ({ onBackToLanding }) => {
  const [me, setMe] = useState<Device>(discovery.getMe());
  const [activeTab, setActiveTab] = useState<AppTab>('discovery');
  const [nearbyDevices, setNearbyDevices] = useState<Device[]>([]);
  const [pinnedDevices, setPinnedDevices] = useState<Device[]>([]);
  const [selectedPeer, setSelectedPeer] = useState<Device | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [editName, setEditName] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showMyQr, setShowMyQr] = useState(false);
  const [networkId, setNetworkId] = useState<string>('Searching...');
  const [isScanning, setIsScanning] = useState(false);
  const [incomingPair, setIncomingPair] = useState<{ id: string, name: string } | null>(null);
  const [peerConnected, setPeerConnected] = useState(false);

  useEffect(() => {
    db.init().then(() => db.getPinnedDevices().then(setPinnedDevices));
    discovery.start();
    discovery.subscribe((devices) => {
      setNearbyDevices(devices);
      setNetworkId(discovery.getNetworkId());
    });
    webrtc.setCallbacks(
      (msg) => {
        setMessages((prev) => [...prev, msg]);
        db.saveMessage(msg);
      },
      (messageId, peerId, progress) => {
        setMessages((prev) => prev.map(m => m.id === messageId ? { ...m, progress } : m));
      },
      (fromId, fromName) => {
        setIncomingPair({ id: fromId, name: fromName });
      },
      (peerId) => {
        // DataChannel is now open — enable sending
        setPeerConnected(true);
      }
    );
    return () => discovery.stop();
  }, []);

  const selectPeer = async (peer: Device | string) => {
    const targetDevice = typeof peer === 'string'
      ? (nearbyDevices.find(d => d.id === peer) || { id: peer, name: 'Connecting...', type: 'mobile', isPinned: false }) as Device
      : peer;
    setPeerConnected(webrtc.isConnected(targetDevice.id)); // may already be open
    setSelectedPeer(targetDevice);
    await webrtc.connectToPeer(targetDevice.id);
    setMessages(await db.getMessages(targetDevice.id));
  };

  const handleAcceptPair = async () => {
    if (incomingPair) {
      const peerId = incomingPair.id;
      const peerName = incomingPair.name;
      setIncomingPair(null);
      setPeerConnected(false); // DC not open yet — onConnected will flip this

      // Send acceptance — the initiator's pair_response handler will call
      // negotiate() and drive the WebRTC OFFER/ANSWER exchange.
      // We must NOT call connectToPeer() here, which would fire another
      // sendPairRequest() back to the initiator, causing an infinite loop.
      discovery.sendPairResponse(peerId, true);

      // Set up the UI to show the chat panel while we wait for the OFFER.
      const targetDevice = nearbyDevices.find(d => d.id === peerId)
        || { id: peerId, name: peerName, type: 'mobile' as const, isPinned: false, isMe: false, connection: 'wifi' as const, lastSeen: Date.now(), latency: 0 };
      setSelectedPeer(targetDevice);
      setMessages(await db.getMessages(peerId));
    }
  };

  const handleDeclinePair = () => {
    if (incomingPair) {
      discovery.sendPairResponse(incomingPair.id, false);
      setIncomingPair(null);
    }
  };

  const handleSendText = () => {
    if (!selectedPeer || !inputText.trim()) return;
    const msg: Message = {
      id: Math.random().toString(36).substr(2, 9),
      senderId: me.id,
      receiverId: selectedPeer.id,
      timestamp: Date.now(),
      type: inputText.startsWith('http') ? 'link' : 'text',
      content: inputText
    };
    webrtc.sendMessage(selectedPeer.id, msg);
    setMessages(prev => [...prev, msg]);
    db.saveMessage(msg);
    setInputText('');
  };

  const handleSendFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0 && selectedPeer) {
      e.target.value = ''; // Reset input so same files can be chosen again
      for (const file of files) {
        const msgId = Math.random().toString(36).substring(2, 11);
        const msg: Message = {
          id: msgId,
          senderId: me.id,
          receiverId: selectedPeer.id,
          timestamp: Date.now(),
          type: 'file',
          content: file.name,
          fileSize: file.size,
          fileType: file.type,
          progress: 0
        };
        setMessages(prev => [...prev, msg]);
        await db.saveMessage(msg);
        await webrtc.sendFile(selectedPeer.id, file, msgId);
      }
    }
  };

  const handleDeleteMessage = async (msgId: string) => {
    if (window.confirm("Permanently delete this item from history?")) {
      await db.deleteMessage(msgId);
      setMessages(prev => prev.filter(m => m.id !== msgId));
    }
  };

  const togglePin = async (device: Device) => {
    if (device.isPinned) {
      await db.unpinDevice(device.id);
      setPinnedDevices(prev => prev.filter(d => d.id !== device.id));
    } else {
      const pinned = { ...device, isPinned: true };
      await db.pinDevice(pinned);
      setPinnedDevices(prev => [...prev, pinned]);
    }
  };

  const updateName = () => {
    if (me.name.trim()) {
      discovery.updateMyName(me.name);
    }
    setEditName(false);
  };

  const triggerScan = () => {
    setIsScanning(true);
    discovery.refresh();
    setTimeout(() => setIsScanning(false), 3000);
  };

  const tabButtonStyle = "px-3 sm:px-6 py-1.5 sm:py-2 rounded-xl text-[9px] sm:text-xs font-black uppercase tracking-widest transition-all";
  const headerContainerStyle = "p-1 rounded-2xl border border-white/[0.06] flex items-center" + " " + "glass-inset";

  return (
    <div className="flex flex-col h-screen overflow-hidden text-[#E5E7EB] dark-gradient selection:bg-[#FF8A00]/30 selection:text-white pt-20">
      <Header onAction={onBackToLanding} setView={onBackToLanding} actionText="Exit Grid" />

      <div className="flex flex-col flex-1 overflow-hidden relative">
        {/* Simplified Header with matching button sizes */}
        <header className="h-12 flex items-center justify-between px-4 sm:px-8 shrink-0 z-40" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(9,11,17,0.88)', backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)' }}>
          {selectedPeer ? (
            <div className={headerContainerStyle}>
              <button onClick={() => setSelectedPeer(null)} className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-gold text-[#0B0E14] rounded-lg active:scale-95 font-mono text-[9px] tracking-widest uppercase font-bold">
                ← Back
              </button>
            </div>
          ) : (
            <div className={headerContainerStyle}>
              <button onClick={() => setActiveTab('discovery')} className={`font-mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-lg transition-all ${activeTab === 'discovery' ? 'bg-orange-gold text-[#0B0E14] font-bold' : 'text-slate-500 hover:text-slate-300'}`}>Connect</button>
              <button onClick={() => setActiveTab('activity')} className={`font-mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-lg transition-all ${activeTab === 'activity' ? 'bg-orange-gold text-[#0B0E14] font-bold' : 'text-slate-500 hover:text-slate-300'}`}>Activity</button>
            </div>
          )}

          <div className={headerContainerStyle}>
            {selectedPeer ? (
              <div className={`${tabButtonStyle} flex items-center gap-2 sm:gap-4 text-white bg-slate-900/50`}>
                <div className="flex flex-col items-end">
                  <p className="truncate max-w-[50px] sm:max-w-[120px]">{selectedPeer.name}</p>
                  <span className="text-[6px] sm:text-[7px] font-bold text-green-500 tracking-tighter uppercase">Direct Link</span>
                </div>
                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-lg bg-orange-gold/10 text-orange-gold flex items-center justify-center shrink-0">
                  <DeviceIcon type={selectedPeer.type} size={12} />
                </div>
              </div>
            ) : (
              <div className={`${tabButtonStyle} flex items-center gap-2 sm:gap-3 text-white group`}>
                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-orange-gold/10 flex items-center justify-center text-orange-gold group-hover:scale-110 transition-transform shrink-0">
                  <UserIcon size={12} />
                </div>
                {editName ? (
                  <input
                    className="bg-transparent border-none outline-none text-[10px] sm:text-xs font-black w-16 sm:w-28 text-white placeholder-slate-600"
                    value={me.name}
                    onChange={e => setMe({ ...me, name: e.target.value })}
                    onBlur={updateName}
                    onKeyDown={e => e.key === 'Enter' && updateName()}
                    autoFocus
                  />
                ) : (
                  <span onClick={() => setEditName(true)} className="cursor-pointer hover:text-orange-gold transition-colors truncate max-w-[60px] sm:max-w-[120px]">{me.name}</span>
                )}
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto relative flex flex-col custom-scrollbar pb-10" style={{ background: 'rgba(11,14,20,0.6)' }}>
          {selectedPeer ? (
            <div className="h-full flex flex-col max-w-5xl mx-auto w-full">
              <div className="flex-1 p-4 sm:p-8 space-y-6 overflow-y-auto custom-scrollbar">
                {messages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center opacity-30 mt-12 px-6">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-slate-900/50 rounded-[2.5rem] sm:rounded-[3rem] flex items-center justify-center mb-6 border border-slate-800 accent-glow">
                      <SendIcon size={32} className="text-orange-gold sm:w-10 sm:h-10" />
                    </div>
                    <p className="text-xs sm:text-sm font-black uppercase tracking-[0.2em] text-white">Grid Link Ready</p>
                    <p className="text-[10px] sm:text-xs text-slate-500 font-medium mt-2">Send a file or message to start the session.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map(msg => (
                      <MessageBubble key={msg.id} msg={msg} isMe={msg.senderId === me.id} onDelete={() => handleDeleteMessage(msg.id)} />
                    ))}
                  </div>
                )}
              </div>
              <footer className="border-t border-white/[0.05] sticky bottom-0 z-30" style={{ background: 'rgba(11,14,20,0.80)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                {/* Connecting banner — visible until DataChannel opens */}
                {!peerConnected && (
                  <div className="flex items-center justify-center gap-2 py-2 px-4" style={{ background: 'rgba(255,138,0,0.08)', borderBottom: '1px solid rgba(255,138,0,0.15)' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-gold animate-pulse shrink-0" />
                    <p className="font-mono text-[9px] tracking-widest uppercase" style={{ color: '#FF8A00' }}>Establishing secure link… please wait</p>
                  </div>
                )}
                <div className="flex items-end space-x-2 sm:space-x-4 max-w-5xl mx-auto p-4 sm:p-8">
                  <label className={`w-11 h-11 sm:w-16 sm:h-16 glass-card rounded-2xl flex items-center justify-center border border-white/[0.06] transition-all shadow-xl shrink-0 ${peerConnected ? 'cursor-pointer hover:border-orange-gold/60 active:scale-90' : 'opacity-40 cursor-not-allowed'}`}>
                    <FileIcon size={20} className="text-orange-gold sm:w-6 sm:h-6" />
                    <input type="file" className="hidden" onChange={handleSendFile} multiple disabled={!peerConnected} />
                  </label>
                  <textarea
                    rows={1}
                    disabled={!peerConnected}
                    className="flex-1 glass-inset rounded-2xl border border-white/[0.05] px-4 sm:px-6 py-3 sm:py-5 text-sm sm:text-base focus:ring-1 focus:ring-orange-gold/60 outline-none resize-none text-white transition-all overflow-hidden disabled:opacity-40"
                    placeholder={peerConnected ? "Type a secure message..." : "Waiting for connection…"}
                    value={inputText}
                    onChange={e => setInputText(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSendText())}
                  />
                  <button
                    onClick={handleSendText}
                    disabled={!peerConnected}
                    className="w-11 h-11 sm:w-16 sm:h-16 bg-orange-gold rounded-2xl flex items-center justify-center text-[#0B0E14] shadow-orange-glow hover:scale-105 active:scale-90 transition-all shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    <SendIcon size={20} className="sm:w-6 sm:h-6" />
                  </button>
                </div>
              </footer>
            </div>
          ) : activeTab === 'activity' ? (
            <div className="max-w-6xl mx-auto w-full p-6 sm:p-10 lg:p-16 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="mb-16">
                <div className="flex items-center justify-between mb-8">
                  <div><p className="font-mono text-[9px] tracking-widest uppercase mb-0.5" style={{ color: '#FF8A00' }}>Pinned</p><h2 className="font-display font-bold text-white text-base" style={{ fontWeight: 600 }}>Pinned Devices</h2></div>
                </div>

                <div className="flex gap-4 sm:gap-6 overflow-x-auto pb-6 px-2 -mx-2 snap-x snap-mandatory scroll-smooth hide-scrollbar">
                  {pinnedDevices.length === 0 ? (
                    <div className="w-full py-12 sm:py-16 px-6 sm:px-8 border border-dashed border-slate-800 rounded-[2rem] sm:rounded-[2.5rem] text-center bg-[#161A22]/30 group hover:border-orange-gold/30 transition-all">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-slate-900 flex items-center justify-center mx-auto mb-4 text-slate-700 group-hover:text-orange-gold transition-colors">
                        <PinIcon size={18} />
                      </div>
                      <p className="text-[9px] sm:text-[10px] text-slate-600 font-black uppercase tracking-widest">Pin your frequent peers here</p>
                    </div>
                  ) : pinnedDevices.map(dev => (
                    <DeviceActivityItem
                      key={dev.id}
                      device={dev}
                      onClick={() => selectPeer(dev)}
                      onPin={() => togglePin(dev)}
                      online={nearbyDevices.some(n => n.id === dev.id)}
                    />
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-8">
                  <div><p className="font-mono text-[9px] tracking-widest uppercase mb-0.5" style={{ color: '#FF8A00' }}>History</p><h2 className="font-display font-bold text-white text-base" style={{ fontWeight: 600 }}>Recent Connections</h2></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <HistoryList onSelectPeer={selectPeer} />
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full flex flex-col items-center animate-in fade-in duration-700 pt-6 sm:pt-16">
              {/* Simplified Connect/Discovery Section */}
              <div className="w-full max-w-4xl mx-auto px-5 sm:px-8 pt-2 pb-4">
                {/* My Device identity strip */}
                <div className="glass-card rounded-2xl p-4 mb-5 relative overflow-hidden" style={{ border: '1px solid rgba(255,138,0,0.14)' }}>
                  <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg,transparent,rgba(255,138,0,0.45),transparent)' }} />
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl glass-inset flex items-center justify-center shrink-0" style={{ border: '1px solid rgba(255,138,0,0.2)', color: '#FF8A00' }}>
                        <DeviceIcon type={me.type} size={16} />
                      </div>
                      <div>
                        <h3 className="font-display text-sm font-bold text-white" style={{ fontWeight: 600 }}>{me.name}</h3>
                        <p className="font-mono text-[8px] tracking-widest uppercase mt-0.5" style={{ color: '#64748B' }}>This device</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${networkId === 'Grid Connected' ? 'bg-green-400' : 'bg-red-500'}`} />
                      <span className="font-mono text-[8px] tracking-widest uppercase" style={{ color: networkId === 'Grid Connected' ? '#4ade80' : '#f87171' }}>{networkId}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full max-w-6xl px-6 sm:px-8 pb-12">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-5">
                  <div className="text-center sm:text-left">
                    <h2 className="font-display text-lg font-bold text-white" style={{ fontWeight: 600 }}>Nearby Peers <span className="font-mono text-[10px] text-slate-500 ml-1">({nearbyDevices.length})</span></h2>
                    <p className="font-mono text-[8px] tracking-widest uppercase mt-0.5" style={{ color: '#64748B' }}>Local network scan</p>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <button onClick={() => setShowScanner(true)} className="flex items-center gap-1.5 px-3 py-2 glass-inset rounded-xl font-mono text-[9px] tracking-widest uppercase transition-all active:scale-95" style={{ color: '#FF8A00', border: '1px solid rgba(255,138,0,0.15)' }}>
                      <CameraIcon size={13} />Scan
                    </button>
                    <button onClick={() => setShowMyQr(true)} className="flex items-center gap-1.5 px-3 py-2 glass-inset rounded-xl font-mono text-[9px] tracking-widest uppercase transition-all active:scale-95" style={{ color: '#FF8A00', border: '1px solid rgba(255,138,0,0.15)' }}>
                      <QRIcon size={13} />My QR
                    </button>
                  </div>
                </div>

                {nearbyDevices.length === 0 ? (
                  <div className="glass-card p-12 sm:p-24 border border-white/[0.05] rounded-[2.5rem] sm:rounded-[3rem] text-center shadow-inner relative group overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-orange-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-[2.5rem] sm:rounded-[3rem]"></div>
                    <div className="w-14 h-14 sm:w-16 sm:h-16 glass-inset rounded-2xl flex items-center justify-center mx-auto mb-6 text-slate-700 shadow-xl group-hover:text-orange-gold transition-colors">
                      <WifiIcon size={24} className="sm:w-8 sm:h-8" />
                    </div>
                    <p className="font-display font-bold text-slate-400 text-sm mb-1" style={{ fontWeight: 600 }}>Scanning for peers</p>
                    <p className="font-mono text-[8px] tracking-wider uppercase mb-6" style={{ color: '#475569' }}>Other devices must have FileSpread open on the same network</p>
                    <button
                      onClick={triggerScan}
                      disabled={isScanning}
                      className={`px-6 py-2.5 rounded-xl font-mono text-[9px] tracking-widest uppercase transition-all active:scale-95 ${isScanning ? 'glass-inset text-slate-500' : 'bg-orange-gold text-[#0B0E14] shadow-orange-glow'}`}
                    >
                      {isScanning ? 'Refreshing...' : 'Refresh'}
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {nearbyDevices.map(dev => (
                      <DeviceDiscoveryItem
                        key={dev.id}
                        device={dev}
                        onClick={() => selectPeer(dev)}
                        onPin={() => togglePin(dev)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <FooterSection />
        </main>
      </div>

      {showScanner && <QRScannerModal onClose={() => setShowScanner(false)} onScan={id => selectPeer(id)} />}
      {showMyQr && <MyQRModal device={me} networkId={networkId} onClose={() => setShowMyQr(false)} />}
      {incomingPair && <PairingRequestModal fromName={incomingPair.name} onAccept={handleAcceptPair} onDecline={handleDeclinePair} />}
    </div>
  );
};

const HistoryList: React.FC<{ onSelectPeer: (id: string) => void }> = ({ onSelectPeer }) => {
  const [history, setHistory] = useState<any[]>([]);

  const loadHistory = async () => {
    await db.init();
    const allMsgs = await (db as any).getAll('messages');
    const peers = new Map();
    allMsgs.sort((a: any, b: any) => b.timestamp - a.timestamp).forEach((m: Message) => {
      const peerId = m.senderId === discovery.getMe().id ? m.receiverId : m.senderId;
      if (!peers.has(peerId)) {
        peers.set(peerId, m);
      }
    });
    setHistory(Array.from(peers.entries()));
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const handleDeleteChat = async (e: React.MouseEvent, peerId: string) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this entire chat session from your local history? This cannot be undone.")) {
      await db.deleteChat(peerId);
      await loadHistory();
    }
  };

  if (history.length === 0) {
    return <p className="text-slate-700 text-[9px] sm:text-[10px] font-black uppercase tracking-widest p-10 sm:p-12 text-center border border-dashed border-slate-800 rounded-[2rem] bg-[#161A22]/20 col-span-full">No session history yet</p>;
  }

  return (
    <>
      {history.map(([peerId, msg]) => (
        <div
          key={peerId}
          onClick={() => onSelectPeer(peerId)}
          className="p-5 glass-card border border-white/[0.06] rounded-2xl flex flex-col gap-4 hover:border-orange-gold/35 transition-all cursor-pointer group shadow-lg relative"
        >
          <div className="flex items-center justify-between">
            <div className="w-9 h-9 sm:w-10 sm:h-10 glass-inset rounded-xl flex items-center justify-center text-slate-500 group-hover:text-orange-gold group-hover:bg-orange-gold/10 transition-all">
              <UserIcon size={18} className="sm:w-5 sm:h-5" />
            </div>
            <div className="flex items-center gap-2">
              <div className="text-[8px] sm:text-[9px] font-black text-slate-600 uppercase tracking-tighter bg-[#0B0E14] px-2 py-1 rounded-lg">
                {new Date(msg.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </div>
              <button
                onClick={(e) => handleDeleteChat(e, peerId)}
                className="w-6 h-6 flex items-center justify-center text-slate-600 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                title="Delete Session"
              >
                <TrashIcon size={14} />
              </button>
            </div>
          </div>
          <div>
            <h4 className="text-xs sm:text-sm font-black text-white truncate mb-1">Peer {peerId.substring(0, 8)}</h4>
            <p className="text-[9px] sm:text-[10px] text-slate-500 font-bold uppercase truncate">
              {msg.type === 'file' ? `📁 ${msg.content}` : `💬 ${msg.content}`}
            </p>
          </div>
          <div className="pt-3 border-t border-slate-800/50 flex justify-end items-center gap-2">
            <span className="text-[8px] font-black text-orange-gold/40 uppercase tracking-widest group-hover:text-orange-gold transition-colors">Re-connect</span>
            <SendIcon size={10} className="text-orange-gold/40 group-hover:text-orange-gold transition-colors" />
          </div>
        </div>
      ))}
    </>
  );
};

const FeatureCard: React.FC<{ icon: React.ReactNode, title: string, description: string }> = ({ icon, title, description }) => (
  <div className="glass-card p-6 rounded-2xl border hover:border-orange-gold/25 transition-all group flex flex-col" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
    <div className="w-10 h-10 glass-inset text-[#FF8A00] rounded-xl flex items-center justify-center mb-5 shrink-0" style={{ border: '1px solid rgba(255,138,0,0.15)' }}>
      <span className="text-[#FF8A00]">{React.cloneElement(icon as React.ReactElement, { size: 18 } as any)}</span>
    </div>
    <h3 className="font-display text-base font-bold text-white mb-2" style={{ fontWeight: 600 }}>{title}</h3>
    <p className="font-body text-slate-500 text-sm leading-relaxed">{description}</p>
  </div>
);

const MessageBubble: React.FC<{ msg: Message; isMe: boolean; onDelete: () => void }> = ({ msg, isMe, onDelete }) => {
  const time = new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return (
    <div className={`flex ${isMe ? 'justify-end' : 'justify-start'} group w-full`}>
      <div className={`relative max-w-[88%] sm:max-w-[72%] rounded-2xl px-4 py-3 shadow-xl ${isMe ? 'bg-orange-gold text-[#0B0E14] rounded-tr-sm' : 'rounded-tl-sm text-[#E5E7EB] border'
        }`} style={!isMe ? { background: 'rgba(20,24,32,0.75)', backdropFilter: 'blur(16px)', borderColor: 'rgba(255,255,255,0.07)' } : undefined}>
        {msg.type === 'file' ? (
          <div className="flex items-center gap-3 py-0.5">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isMe ? 'bg-black/10' : 'glass-inset text-[#FF8A00]'}`}>
              <FileIcon size={18} />
            </div>
            <div className="min-w-0">
              <p className="font-display text-xs font-bold truncate" style={{ fontWeight: 600 }}>{msg.content}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-mono text-[8px] tracking-wider uppercase opacity-60">{(msg.fileSize! / 1024 / 1024).toFixed(2)} MB</span>
                {msg.progress !== undefined && msg.progress < 100 && (
                  <div className="flex-1 h-0.5 min-w-[36px] bg-black/15 rounded-full overflow-hidden">
                    <div className="h-full bg-current opacity-50 transition-all" style={{ width: `${msg.progress}%` }} />
                  </div>
                )}
                {msg.progress === 100 && <span className="font-mono text-[7px] tracking-widest uppercase opacity-60">Done</span>}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{msg.content}</p>
        )}
        <div className="flex items-center justify-between mt-2 gap-4" style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: '6px' }}>
          <button onClick={onDelete} className={`font-mono text-[8px] tracking-widest uppercase flex items-center gap-1 transition-all opacity-0 group-hover:opacity-100 hover:text-red-500 ${isMe ? 'text-black/30' : 'text-slate-600'}`}>
            <TrashIcon size={10} /> Delete
          </button>
          <p className={`font-mono text-[8px] tracking-widest uppercase ${isMe ? 'text-black/35' : 'text-slate-600'}`}>{time}</p>
        </div>
      </div>
    </div>
  );
};

const DeviceActivityItem: React.FC<{ device: Device; onClick: () => void; onPin: () => void; online: boolean }> = ({ device, onClick, onPin, online }) => (
  <div onClick={online ? onClick : undefined} className={`snap-start min-w-[160px] sm:min-w-[200px] p-4 glass-card rounded-2xl flex flex-col items-center gap-4 transition-all shrink-0 shadow-xl peer-card ${online ? 'cursor-pointer' : 'opacity-40 grayscale pointer-events-none'}`} style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
    <div className="relative">
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all shadow-lg ${online ? 'bg-orange-gold text-[#0B0E14]' : 'glass-inset text-slate-500'}`}>
        <DeviceIcon type={device.type} size={26} />
      </div>
      {online && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-green-400 border-2 border-[#0B0E14] rounded-full" />}
    </div>
    <div className="text-center w-full">
      <h4 className="font-display text-xs font-bold text-white truncate" style={{ fontWeight: 600 }}>{device.name}</h4>
      <p className="font-mono text-[8px] tracking-widest uppercase mt-1" style={{ color: online ? '#4ade80' : '#64748B' }}>{online ? 'Online' : 'Offline'}</p>
    </div>
    <div className="flex items-center justify-between w-full pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <button onClick={e => { e.stopPropagation(); onPin(); }} className="text-[#FF8A00] opacity-40 hover:opacity-100 transition-all p-1" title="Unpin">
        <PinIcon filled={device.isPinned} size={14} />
      </button>
      {online && <span className="font-mono text-[7px] tracking-widest uppercase" style={{ color: 'rgba(255,138,0,0.7)' }}>Connect →</span>}
    </div>
  </div>
);

const DeviceDiscoveryItem: React.FC<{ device: Device; onClick: () => void; onPin: () => void }> = ({ device, onClick, onPin }) => (
  <div onClick={onClick} className="p-4 glass-card rounded-2xl peer-card flex items-center justify-between shadow-lg cursor-pointer" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-10 h-10 glass-inset rounded-xl flex items-center justify-center shrink-0 transition-all group-hover:bg-[#FF8A00] group-hover:text-[#0B0E14]" style={{ color: '#FF8A00', border: '1px solid rgba(255,138,0,0.15)' }}>
        <DeviceIcon type={device.type} size={18} />
      </div>
      <div className="min-w-0">
        <h4 className="font-display text-sm font-bold text-white truncate" style={{ fontWeight: 600 }}>{device.name}</h4>
        <p className="font-mono text-[8px] tracking-widest uppercase mt-0.5" style={{ color: '#64748B' }}>Local peer</p>
      </div>
    </div>
    <div className="flex items-center gap-1.5 shrink-0">
      <button onClick={e => { e.stopPropagation(); onPin(); }} className={`p-2 rounded-lg transition-all ${device.isPinned ? 'text-[#FF8A00]' : 'text-slate-600 hover:text-[#FF8A00]'}`} title={device.isPinned ? 'Unpin' : 'Pin'}>
        <PinIcon filled={device.isPinned} size={14} />
      </button>
      <div className="w-8 h-8 rounded-xl glass-inset flex items-center justify-center" style={{ color: '#FF8A00', border: '1px solid rgba(255,138,0,0.12)' }}>
        <SendIcon size={14} />
      </div>
    </div>
  </div>
);

const PrivacyPolicyContent = () => (
  <div className="space-y-6 text-gray-300 leading-relaxed">
    <p>
      At <span className="text-white font-medium">FileSpread</span>, privacy is a core principle.
      The application is designed to enable direct, peer-to-peer sharing without collecting,
      storing, or monitoring your personal data or shared content.
    </p>

    <h3 className="text-white font-semibold text-lg">No File Storage</h3>
    <p>
      FileSpread does not store your files on any server. All files, messages, and shared data
      are transferred directly between devices using peer-to-peer (P2P) connections.
      We do not have access to the content you share.
    </p>

    <h3 className="text-white font-semibold text-lg">Peer-to-Peer Transfers</h3>
    <p>
      File transfers are performed using WebRTC, allowing data to flow directly between
      participating devices. Once a connection is established, communication does not pass
      through FileSpread servers.
    </p>

    <h3 className="text-white font-semibold text-lg">Signaling Only</h3>
    <p>
      A minimal signaling server is used solely to help devices discover each other and
      establish a connection. This server does not store files, messages, or transfer data,
      and does not inspect shared content.
    </p>

    <h3 className="text-white font-semibold text-lg">No Tracking or Analytics</h3>
    <p>
      FileSpread does not use tracking cookies, analytics tools, or advertising services.
      Your activity within the app is not monitored or profiled.
    </p>

    <h3 className="text-white font-semibold text-lg">Your Control</h3>
    <p>
      All sharing sessions are initiated and controlled by you. You can end a connection
      at any time, and shared data exists only on the participating devices.
    </p>

    <p className="text-sm text-gray-400">
      By using FileSpread, you acknowledge that peer-to-peer transfers depend on browser
      and network conditions. FileSpread is provided as-is, with a focus on transparency,
      privacy, and local-first design.
    </p>
  </div>
);


const TermsContent = () => (
  <div className="space-y-6 text-gray-300 leading-relaxed">
    <p>
      By accessing or using <span className="text-white font-medium">FileSpread</span>, you agree
      to use the application responsibly and in compliance with applicable laws and regulations.
      You are solely responsible for the content you choose to share.
    </p>

    <h3 className="text-white font-semibold text-lg">Acceptable Use</h3>
    <ul className="list-disc pl-5 space-y-2">
      <li>Use FileSpread only for lawful purposes.</li>
      <li>Do not share malware, harmful software, or unauthorized copyrighted material.</li>
      <li>Ensure you have permission to share any content you transmit.</li>
    </ul>

    <h3 className="text-white font-semibold text-lg">Service Availability</h3>
    <p>
      FileSpread relies on peer-to-peer connections and a minimal signaling service.
      We do not guarantee uninterrupted availability, connection stability, or transfer success.
    </p>

    <h3 className="text-white font-semibold text-lg">User Responsibility</h3>
    <p>
      You are responsible for securing your devices and networks while using FileSpread.
      File transfers occur directly between peers, and FileSpread does not monitor or control
      shared content.
    </p>

    <h3 className="text-white font-semibold text-lg">Limitation of Liability</h3>
    <p>
      FileSpread is provided “as is” without warranties of any kind. We are not liable for
      data loss, connection failures, or damages resulting from use of the service.
    </p>
  </div>
);


const DisclaimerContent = () => (
  <div className="space-y-6 text-gray-300 leading-relaxed">
    <p>
      <span className="text-white font-medium">FileSpread</span> is provided on an “as is” and
      “as available” basis by Dhakad Labs, without warranties of any kind, express or implied.
    </p>

    <p>
      FileSpread relies on peer-to-peer connections and local network conditions.
      Performance, reliability, and transfer success may vary depending on device,
      browser, and network configuration.
    </p>

    <p>
      Dhakad Labs is not responsible for data loss, failed transfers, security issues,
      or damages resulting from the use or inability to use FileSpread.
    </p>
  </div>
);


const CopyrightPolicyContent = () => (
  <div className="space-y-6 text-gray-300 leading-relaxed">
    <p>
      © 2026 <span className="text-white font-medium">Dhakad Labs</span>.
      The FileSpread name, logo, branding, and original source code are the intellectual
      property of Dhakad Labs, unless otherwise stated.
    </p>

    <p>
      FileSpread does not host, store, or distribute user content. All file transfers
      occur directly between users’ devices over peer-to-peer connections.
    </p>

    <p>
      Users are responsible for ensuring they have the legal right to share any content.
      Dhakad Labs does not monitor, control, or have the ability to remove files shared
      between peers.
    </p>
  </div>
);


const ContactContent = () => (
  <div className="space-y-6 text-slate-400 leading-relaxed">
    <p>
      Have questions, feedback, or ideas for new features?
      We’d love to hear from you.
    </p>

    <div className="bg-[#161A22] p-6 sm:p-8 rounded-[2rem] border border-slate-800">
      <p className="text-slate-200 font-semibold mb-2">
        Get in Touch
      </p>
      <p className="text-slate-400 text-sm sm:text-base mb-6">
        Reach out for support, feature requests, collaboration ideas,
        or general questions about FileSpread and Dhakad Labs.
      </p>

      {/* Contact Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <a
          href="mailto:dhakadlabs@gmail.com"
          className="
    relative px-6 py-3 rounded-full
    bg-[#0B0F1A] text-white
    before:absolute before:inset-[-2px]
    before:rounded-full
    before:bg-gradient-to-r
    before:from-pink-500 before:to-purple-500
    before:blur-sm
    before:opacity-70
    before:-z-10
    hover:before:opacity-100
    transition
  "
        >
          Email Us
        </a>

        <a
          href="https://www.instagram.com/127.dhakad.1"
          target="_blank"
          rel="noopener noreferrer"
          className="
    relative px-6 py-3 rounded-full
    bg-[#0B0F1A] text-white
    before:absolute before:inset-[-2px]
    before:rounded-full
    before:bg-gradient-to-r
    before:from-pink-500 before:to-purple-500
    before:blur-sm
    before:opacity-70
    before:-z-10
    hover:before:opacity-100
    transition
  "
        >
          Message on Instagram
        </a>
      </div>
    </div>
  </div>
);



const AboutPage: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <div className="min-h-screen dark-gradient text-[#E5E7EB] selection:bg-[#FF8A00]/30 pt-24 flex flex-col">
    <Header onAction={onBack} setView={() => onBack()} actionText="Back Home" />

    <main className="max-w-4xl mx-auto px-6 py-12 flex-1 w-full">
      {/* Hero */}
      <div className="text-center mb-12">
        <p className="font-mono text-[10px] tracking-widest uppercase mb-3" style={{ color: '#FF8A00' }}>Laboratory</p>
        <h1 className="font-display text-3xl sm:text-5xl font-bold text-white mb-4 tracking-tight" style={{ fontWeight: 700 }}>
          About Dhakad Labs
        </h1>
        <p className="font-body text-slate-400 text-sm max-w-xl mx-auto leading-relaxed">
          Building high-performance, privacy-first software through direct, honest engineering.
        </p>
      </div>

      <div className="mb-10" style={{ height: '1px', background: 'linear-gradient(90deg,transparent,rgba(255,138,0,0.25),transparent)' }} />

      {/* Mission + Logo */}
      <div className="grid md:grid-cols-2 gap-8 items-center mb-16">
        <div className="space-y-4 font-body text-sm text-slate-400 leading-relaxed">
          <h2 className="font-display text-lg font-bold text-white" style={{ fontWeight: 600 }}>
            Our Mission
          </h2>

          <p>
            Dhakad Labs is an independent development initiative focused on creating
            simple, meaningful, and reliable software. The goal is to solve everyday
            problems using honest technology — without unnecessary complexity,
            subscriptions, or hidden telemetry.
          </p>

          <p>
            Projects developed under Dhakad Labs are built with a strong emphasis on
            local-first architecture, absolute privacy, and real-world usability. Wherever possible,
            your data stays directly on your machine and never hits a centralized relay.
          </p>

          <p>
            One of our core utilities is{" "}
            <span className="text-white font-medium">FileSpread</span>, a direct, browser-native P2P
            data transmission tool that utilizes WebRTC signaling to connect local devices securely.
          </p>
        </div>

        <div className="bg-[#10131a] glass-card rounded-2xl p-8 flex items-center justify-center border" style={{ borderColor: 'rgba(255,138,0,0.12)', boxShadow: '0 0 30px rgba(255,138,0,0.05)' }}>
          <DhakadLabsLogo size={180} className="w-40 h-40 sm:w-48 sm:h-48" />
        </div>
      </div>

      {/* Contact & Support */}
      <div className="glass-card rounded-2xl p-8 text-center border relative overflow-hidden" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg,transparent,rgba(255,138,0,0.3),transparent)' }} />
        <h2 className="font-display text-base font-bold text-white mb-3" style={{ fontWeight: 600 }}>
          Connect With Us
        </h2>

        <p className="font-body text-slate-400 text-xs max-w-xl mx-auto mb-8 leading-relaxed">
          Dhakad Labs is driven by learning, experimentation, and community feedback.
          Feel free to reach out, explore our work, or follow along the journey.
        </p>

        {/* Contact Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
          <a
            href="mailto:dhakadlabs@gmail.com"
            className="px-5 py-2 glass-inset rounded-lg font-mono text-[10px] tracking-widest uppercase transition-all hover:text-white"
            style={{ border: '1px solid rgba(255,255,255,0.08)' }}
          >
            Email
          </a>

          <a
            href="https://www.instagram.com/127.dhakad.1"
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-2 glass-inset rounded-lg font-mono text-[10px] tracking-widest uppercase transition-all hover:text-white"
            style={{ border: '1px solid rgba(255,255,255,0.08)' }}
          >
            Instagram
          </a>

          <a
            href="https://dhakadlabs.site"
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-2 glass-inset rounded-lg font-mono text-[10px] tracking-widest uppercase transition-all hover:text-white"
            style={{ border: '1px solid rgba(255,255,255,0.08)' }}
          >
            Website
          </a>
        </div>

        {/* Support Button */}
        <div className="inline-block">
          <a
            href="https://github.com/sponsors/your-github-username"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-orange-gold text-[#0B0E14] font-mono text-[10px] tracking-widest uppercase rounded-lg font-bold hover:scale-105 active:scale-95 shadow-orange-glow transition-all"
          >
            Support Our Work <span className="text-[12px] leading-none">❤️</span>
          </a>
          <p className="font-mono text-[8px] text-slate-500 uppercase tracking-widest mt-3">
            All projects are free to use · support helps sustain development
          </p>
        </div>
      </div>
    </main>

    <FooterSection />
  </div>
);



export default App;
